import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { films } from '@/mocks/films';

export default function HeroBanner() {
  const featuredFilms = films.filter(f => f.featured);
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchRef = useRef({ startX: 0, endX: 0 });

  const startAuto = () => {
    stopAuto();
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % featuredFilms.length);
    }, 5000);
  };

  const stopAuto = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    startAuto();
    return stopAuto;
  }, [featuredFilms.length]);

  const goTo = (idx: number) => {
    setCurrent(idx);
    startAuto();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchRef.current.startX = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchRef.current.endX = e.changedTouches[0].clientX;
    const diff = touchRef.current.startX - touchRef.current.endX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setCurrent((prev) => (prev + 1) % featuredFilms.length);
      } else {
        setCurrent((prev) => (prev - 1 + featuredFilms.length) % featuredFilms.length);
      }
      startAuto();
    }
  };

  const film = featuredFilms[current];

  if (!film) return null;

  return (
    <section
      className="relative w-full h-[420px] md:h-[550px] overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={stopAuto}
      onMouseLeave={startAuto}
    >
      {featuredFilms.map((f, idx) => (
        <div
          key={f.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === current ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={f.coverImage}
            alt={f.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50/85 via-background-50/40 to-background-50/60"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/30 to-transparent"></div>
        </div>
      ))}

      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
          <div className="max-w-2xl">
            <span className="inline-block bg-primary-500 text-background-50 text-xs font-bold px-3 py-1 rounded-full mb-4 whitespace-nowrap">
              {film.status}
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 mb-3 leading-tight">
              {film.title}
            </h2>
            <p className="text-foreground-100 text-sm md:text-base mb-4 line-clamp-2 leading-relaxed max-w-lg">
              {film.description.slice(0, 100)}...
            </p>
            <div className="flex items-center gap-1 mb-3 flex-wrap">
              <span className="text-foreground-200 text-xs bg-background-50/20 px-2 py-0.5 rounded whitespace-nowrap">{film.subcategory}</span>
              <span className="text-foreground-300 text-xs">·</span>
              <span className="text-foreground-200 text-xs whitespace-nowrap">{film.region}</span>
              <span className="text-foreground-300 text-xs">·</span>
              <span className="text-foreground-200 text-xs whitespace-nowrap">{film.year}</span>
              <span className="text-foreground-300 text-xs">·</span>
              <span className="text-primary-400 text-xs font-semibold whitespace-nowrap">{film.rating} 分</span>
            </div>
            <Link
              to={`/article/${film.id}`}
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-play-fill"></i>
              </div>
              立即观看
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-6 z-10 flex items-center gap-1.5">
        {featuredFilms.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              idx === current
                ? 'bg-primary-500 w-6'
                : 'bg-foreground-50/40 hover:bg-foreground-50/70'
            }`}
            aria-label={`幻灯片 ${idx + 1}`}
          />
        ))}
      </div>

      <button
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 hover:bg-background-50/40 text-foreground-50 flex items-center justify-center transition-colors cursor-pointer"
        onClick={() => {
          setCurrent((prev) => (prev - 1 + featuredFilms.length) % featuredFilms.length);
          startAuto();
        }}
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>

      <button
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 hover:bg-background-50/40 text-foreground-50 flex items-center justify-center transition-colors cursor-pointer"
        onClick={() => {
          setCurrent((prev) => (prev + 1) % featuredFilms.length);
          startAuto();
        }}
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}